/** Supported brokerage custodians for investment accounts. Used in account setup dropdowns. */
export const CUSTODIANS = [
  { value: "Fidelity Investments", label: "Fidelity Investments" },
  { value: "Vanguard", label: "Vanguard" },
  { value: "Charles Schwab", label: "Charles Schwab" },
  { value: "E*TRADE", label: "E*TRADE" },
  { value: "TD Ameritrade", label: "TD Ameritrade" },
  { value: "Merrill Edge", label: "Merrill Edge" },
  { value: "Robinhood", label: "Robinhood" },
  { value: "Webull", label: "Webull" },
  { value: "SoFi Invest", label: "SoFi Invest" },
  { value: "Ally Invest", label: "Ally Invest" },
  { value: "Other", label: "Other" }
];

/**
 * Financial calculation constants for the YouVest investment tool.
 * RATE_OF_RETURN / INDUSTRY_AUM are used in fee savings projections.
 * FILLER_ETF is the default ETF used to fill small allocation gaps.
 * MINIMUM_INVESTMENT_AMOUNT is the minimum USD to invest.
 */
export const YOUVEST_VARIABLES = {
  FEE_SAVINGS: {
    RATE_OF_RETURN: 0.085,
    INDUSTRY_AUM: 0.023,
  },
  INDUSTRY_AUM: 0.023,
  FILLER_ETF: {
    TICKER: "SFY",
    NAME: "SoFi Select 500 ETF",
    EXPENSE_RATIO: 0,
  },
  MINIMUM_INVESTMENT_AMOUNT: 100,
};

/** Starting cumulative fee savings amount displayed on the platform dashboard. */
export const FEE_SAVINGS = {
  STARTING_FEES_SAVED: 1430000,
};

/** Role type identifiers for permission checks throughout the app. */
export const AUTHORIZATION = {
  ADVISOR: "advisor",
  ADMIN: "admin",
  CONTENT_EDITOR: "content_editor",
  BLOG_EDITOR: "blog_editor",
  TRAVEL_LOUNGE_EDITOR: "travel_lounge_editor",
  LEARNING_LAB_EDITOR: "learning_lab_editor",
};

/** Valid status string values for tasks, chapters, and ongoing advice records. */
export const STATUS_OPTIONS = {
  TASKS: {
    ACTIVE: 'active',
    COMPLETED: 'completed',
    ARCHIVED: 'archived',
    HIGHLIGHTED: 'highlighted',
  },
  CHAPTERS: {
    ACTIVE: 'active',
    COMPLETED: 'completed',
    FUTURE: 'future',
    UNPUBLISHED: 'unpublished',
    ARCHIVED: 'archived',
  },
  ONGOING_ADVICE: {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
  }
}

/** Item type identifiers for react-dnd drag-and-drop interactions. */
export const DRAG_N_DROP_TYPES = {
  TASK: "task",
  CHAPTER: "chapter",
  GOAL: "goal",
};

/** User activity types recorded in the activity log. */
export const ACTIVITY_TYPES = {
  INVESTMENT: "Investment",
  LOGIN: "Login",
};

/** Risk tolerance questionnaire structure used during investment account setup. */
export const RISK_TOLERANCE_QUESTIONS = {
  ACCOUNT: {
    Q1: {
      QUESTION: "I plan to begin taking money from my investments in...",
      ANSWERS: {
        0: "1 year or less",
        1: "3-5 years",
        2: "6-10 years",
        3: "11-15 years",
        4: "16+ years",
      },
    },
    Q2: {
      QUESTION:
        "  As I withdraw my money from these investments, I plan to spend it over a period of...",
      ANSWERS: {
        0: "1 year or less",
        1: "3-5 years",
        2: "6-10 years",
        3: "11-15 years",
        4: "16+ years",
      },
    },
  },

  PERSONAL: {
    Q1: {
      QUESTION:
        "Q1. Generally, I prefer an investment with little to no ups and downs in value and I am willing to accept the lower returns these investments may make.",
      ANSWERS: {
        0: "Strongly Agree",
        1: "Agree",
        2: "Somewhat Agree",
        3: "Disagree",
        4: "Strongly Disagree",
      },
    },
    Q2: {
      QUESTION:
        "  Q2. If I owned a stock investment that lost 30% of its value during the timespan of one month, I would:",
      ANSWERS: {
        0: "Sell all of the remaining investment",
        1: "Sell some of the remaining investment",
        2: "Hold onto the investment and sell nothing",
        3: "Buy more of the investment",
      },
    },
    Q3: {
      QUESTION:
        "Q3. The chart below shows the highest one-year loss and the highest one-year gain on three different investments of $10,000. Given the potential gain or loss in any one year, I would invest my money in:",
      ANSWERS: { 0: "Investment A", 1: "Investment B", 2: "Investment C" },
    },
    Q4: {
      QUESTION:
        "Q4. When it comes to investing in mutual funds (or individual stocks or bonds), I would describe myself as...",
      ANSWERS: {
        0: "Very inexperienced",
        1: "Somewhat inexperienced",
        2: "Somewhat experienced",
        3: "Experienced",
        4: "Very experienced",
      },
    },
  },
};

/** Maps Ethos asset class labels to internal display names used in portfolio views. */
export const ETHOS_ASSET_CLASSES = {
  "Bond - World": "International Bonds",
  "Bond - High Yield": "High Yield",
  "Bond - Short-Term": "Corporate",
  "Bond - Corporate": "Corporate",
  "Bond - Short Government": "Government",
  "Bond - Intermediate Government": "Government",
  "Single Sector - Real Estate": "Real Estate",
  "Equity - Emerging Markets": "Emerging Market",
  "Equity - Ex-US Large Blend": "International Developed",
  "Equity - US Small Blend": "Small Cap",
  "Equity - US Small Growth": "Small Cap",
  "Equity - US Small Value": "Small Cap",
  "Equity - US Mid-Cap Blend": "Mid Cap",
  "Equity - US Mid-Cap Value": "Mid Cap",
  "Equity - US Mid-Cap Growth": "Mid Cap",
  "Equity - US Large Blend": "Large Blend",
  "Equity - US Large Growth": "Large Growth",
  "Equity - US Large Value": "Large Value",

  "Bond - Inflation-Protected": "TIPS",
};

/** Display sort order for asset classes in portfolio allocation views (ascending). */
export const ASSET_CLASSES_ORDER = {
  "Large Blend": 1,
  "Large Growth": 2,
  "Large Value": 3,
  "Mid Cap": 4,
  "Small Cap": 5,
  "International Developed": 6,
  "Emerging Market": 7,
  "Real Estate": 8,
  Government: 9,
  Corporate: 10,
  "High Yield": 11,
  "International Bonds": 12,
  TIPS: 13,
};

/** External document URLs for SEC filings and compliance disclosures. */
export const EXTERNAL_LINKS = {
  ADV_PART_2A: 'https://files.adviserinfo.sec.gov/IAPD/Content/Common/crd_iapd_Brochure.aspx?BRCHR_VRSN_ID=1000557',
  ADV_PART_2B: 'https://drive.google.com/file/d/1o_oXCCGaeccAWkDHSEu8RrVvL_PrSlvB/view?usp=drive_link',
  FORM_CRS: 'https://reports.adviserinfo.sec.gov/crs/crs_304612.pdf',
  PRIVACY_POLICY: '/privacy-policy',
}
